<template>
	<div class="SectionMenu" :class="classes">
		<!-- @slot for the section menu-->
		<slot />
	</div>
</template>

<script>
export default {
	props: {
		reverse: {
			type: Boolean,
			default: false,
		},
		color: {
			type: String,
			default: '',
		},
		small: {
			type: Boolean,
			default: false,
		},
		noseparators: {
			type: Boolean,
			default: false,
		},
		uppercaseHeadings: {
			type: Boolean,
			default: false,
		},
		flush: {
			type: Boolean,
			default: false,
		},
		strong: {
			type: Boolean,
			default: false,
		},
		ruleBelow: {
			type: Boolean,
			default: false,
		},
		horizontal: {
			type: Boolean,
			default: false,
		},
		nested: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes() {
			let classes = [];
			const prefix = 'SectionMenu--';
			if (this.reverse) {
				classes.push(prefix + 'reverse');
			}
			if (this.small) {
				classes.push(prefix + 'small');
			}
			if (this.noseparators) {
				classes.push(prefix + 'noSeparators');
			}
			if (this.uppercaseHeadings) {
				classes.push(prefix + 'uppercaseHeadings');
			}
			if (this.flush) {
				classes.push(prefix + 'flush');
			}
			if (this.strong) {
				classes.push(prefix + 'strong');
			}
			if (this.ruleBelow) {
				classes.push(prefix + 'ruleBelow');
			}
			if (this.horizontal) {
				classes.push(prefix + 'horizontal');
			}
			if (this.nested) {
				classes.push(prefix + 'nested');
			}
			if (this.color.length) {
				classes.push(prefix + this.color + 'Light');
			}
			return classes.join(' ');
		},
	},
};
</script>

<style lang="scss">
.SectionMenu {
	@include stack-md;
	@include inset-md;
}

.SectionMenu-section {
	@include stack-md;
	max-width: 100%;
	&:not(:only-child) {
		padding-top: $space-sm;
		border-top: 1px solid $color-neutral-1;
	}
	&--noSeparator {
		padding-top: 0 !important;
		border: none !important;
	}
}

.SectionMenu-section-heading {
	@include stack-xs;
	@include text(4);
	color: $color-text-muted;
	a.is-current {
		color: $color-black;
		font-weight: bold;
	}
	&--uppercase {
		@include text(3);
		text-transform: uppercase;
	}
}

.SectionMenu-section-list {
	list-style-type: none;
	margin: 0;
	padding: 0;
	@include text(3);
	& > li {
		padding: $space-xs 0;
		color: $color-text-muted;
	}
	& > li > a {
		&.is-current {
			color: $color-text;
			font-weight: bold;
		}
	}
}

/* ----------------
	Variants
---------------- */

/* Reverse variant */
.SectionMenu--reverse {
	.SectionMenu-section {
		&:not(:only-child) {
			border-top: 1px solid rgba(255, 255, 255, 0.15);
		}
	}
	.SectionMenu-section-heading {
		color: $color-neutral-2;
		a {
			color: $color-link-reverse;
			&:hover,
			&:focus,
			&:active {
				color: $color-link-hover-reverse;
			}
		}
		a.is-current {
			color: $color-white;
		}
	}
	.SectionMenu-section-list {
		& > li {
			color: $color-neutral-2;
		}
		& > li > a {
			color: $color-link-reverse;
			&:hover,
			&:focus,
			&:active {
				color: $color-link-hover-reverse;
			}
			&.is-current {
				color: $color-text-reverse;
			}
		}
	}
}

/* Small variant */
.SectionMenu--sm {
	.SectionMenu-section {
		@include stack-sm;
		&:not(:only-child) {
			padding-top: $space-xs;
			border-top: 1px solid $color-neutral-1;
		}
	}
	.SectionMenu-section-heading {
		margin-bottom: calc(#{$space-xs}/ 2);
		@include text(3);
		&--uppercase {
			@include text(2);
		}
	}
	.SectionMenu-section-list {
		@include text(2);
		& > li {
			padding: $space-xs 0;
		}
	}
}

/* No borders variant */
.SectionMenu--noSeparators {
	.SectionMenu-section {
		padding-top: 0 !important;
		border: none !important;
	}
}

/* Uppercase Headings variant */
.SectionMenu--uppercaseHeadings {
	.SectionMenu-section-heading {
		@include text(3);
		text-transform: uppercase;
	}
	&.SectionMenu--sm {
		.SectionMenu-section-heading {
			@include text(2);
			text-transform: uppercase;
		}
	}
}

/* Flush variant */
.SectionMenu--flush {
	margin: 0;
	padding: 0;
}

/* Primary Light variant */
.SectionMenu--primaryLight {
	background-color: $color-primary-0;
	.SectionMenu-section {
		&:not(:only-child) {
			border-top: 1px solid $color-primary-1;
		}
	}
}

/* Neutral Light variant */
.SectionMenu--neutralLight {
	background-color: $color-neutral-0;
	.SectionMenu-section {
		&:not(:only-child) {
			border-top: 1px solid $color-neutral-1;
		}
	}
}

/* Horizontal variant - flows inline where space is available */
.SectionMenu--horizontal {
	// Flex layout
	display: flex;
	flex-wrap: wrap;
	.SectionMenu-section {
		@include inline-md;
		margin-bottom: $space-md;
		flex-grow: 1;
		flex-basis: 12rem;
		&--fullRow {
			flex-basis: 100%;
		}
	}
	// Grid layout
	@supports (display: grid) {
		display: grid;
		margin-bottom: 0;
		grid-column-gap: $space-lg;
		grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
		.SectionMenu-section {
			@include stack-md;
			&--fullRow {
				grid-column: 1 / -1;
			}
		}
	}
	&.SectionMenu--sm {
		.SectionMenu-section {
			@supports (display: grid) {
				@include stack-sm;
			}
		}
	}
}

.SectionMenu--nested {
	@include breakpoint(medium) {
		// some reasonable limit so the nested list is seen when component is not in a narrow column
		// this is just for display in lichen
		max-width: 40rem;
		.SectionMenu-section {
			margin: 0;
			border-bottom: 1px solid $brand-color-sand-shade !important;
		}
		.SectionMenu-section {
			&:not(:only-child) {
				padding-top: 0;
				border-top: 0;
			}
		}
		.SectionMenu-section-list {
			& > li > a {
				color: $color-black;
				display: flex;
				gap: $space-md;
				align-items: center;
				justify-content: space-between;
				@include inset-squish-md;
				&:hover,
				&.is-current {
					background-color: $brand-color-sand-tint-1;
					color: $color-black;
				}
				&.is-current {
					font-weight: 900;
				}
			}
			// nested list, only shown on hover
			& > li > .SectionMenu-section-list {
				z-index: 1;
				@include inset-sm;
				display: none;
				& > li > a {
					white-space: nowrap;
					margin: 0;
					@include inset-squish-sm;
					&:hover {
						background-color: $brand-color-sand-tint-1;
					}
				}
			}
			& > li:hover {
				position: relative;
				& > .SectionMenu-section-list {
					display: block;
					position: absolute;
					left: 100%;
					[dir='rtl'] & {
						left: auto;
						right: 100%;
					}
					top: 0;
					min-width: 12rem;
					background-color: $brand-color-sand;
				}
			}
			// when parent is current, show nested list inline
			& > li.is-current > .SectionMenu-section-list {
				display: block;
				margin-inline-start: $space-lg;
				padding: 0;
				position: relative;
				left: auto;
				[dir='rtl'] & {
					right: auto;
				}
				top: auto;
				min-width: auto;
				//background-color: transparent;
				& > li > a {
					white-space: initial;
				}
			}
		}
	}
}

.SectionMenu--strong {
	.SectionMenu-section-list {
		& > li {
			font-weight: bold;
			& > .SectionMenu-section-list {
				& > li {
					font-weight: normal;
				}
			}
		}
	}
}

.SectionMenu--ruleBelow {
	.SectionMenu-section {
		&:not(:only-child) {
			padding-top: 0;
			border-top: 0;
			padding-bottom: $space-sm;
			border-bottom: 1px solid $color-neutral-1;
		}
	}
}
</style>
